<template lang="pug" src="./medium.pug"></template>

<i18n>
{
  "de": {
    "AI labelling": "KI-Kennzeichnung",
    "AI labelled": "Als KI gekennzeichnet",
    "Close": "Schließen",
    "Product list": "Liste",
    "Product page": "Produktseite",
    "Highlight": "Highlight",
    "Not in gallery": "Nicht in Galerie",
    "AI Edit": "Mit KI bearbeiten",
    "Edit": "Bearbeiten",
    "Edited (has variants)": "Bearbeitet (hat Varianten)",
    "Duplicate": "Duplizieren",
    "Download": "Herunterladen",
    "Video": "Video",
    "Download failed": "Download fehlgeschlagen"
  }
}
</i18n>

<script>
import _ from 'lodash';
import uniqid from 'uniqid';

// import store from '@/store';
import pinia from '@/pinia';
import { useSetupStore, useShopProductStore } from '@/stores';
import AuthApi from '@/store/api/system/auth';
import { ensureFreshToken } from '@/helpers';
import VideoConversionBadge from '@/components/VideoConversionBadge';
import HlsVideoPlayer from './HlsVideoPlayer';
import AiDisclosureField from '@/extensions/generative-media/components/AiDisclosureField';
import { getFitStringFromProcessObject } from '@/helpers';

export default {
    name: 'ProductMedium',
    components: {
        VideoConversionBadge,
        HlsVideoPlayer,
        AiDisclosureField,
    },
    props: {
        form: {
            type: Object,
            required: true
        },
        media: {
            type: Object,
            required: true
        },
        scope: {
            type: String,
            default: 'form' // stepper / form
        },
        imageEditEnabled: {
            type: Boolean,
            default: false
        },
        // Progress (0–100) of a running AI edit on this tile's image, or null when
        // idle. Renders a minimal progress overlay on the thumbnail.
        generationProgress: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            videoDialogVisible: false,
            aiDrawerVisible: false,
            // The drawer edits a DETACHED copy. Nothing reaches the media object until
            // the commit button is pressed, so Close discards - same contract as the
            // image editor drawer.
            aiDraft: null,
            // HLS file captured from the conversion-completed broadcast. Held locally
            // because updating the nested `media.video.hls` prop does NOT reliably
            // invalidate the computeds below (the media prop's deep reactivity doesn't
            // propagate here); a local reactive property always does.
            localHls: null
        };
    },
    computed: {
        // Names the asset being labelled, so the drawer is unambiguous when it is
        // opened from an article's media rather than the product's.
        aiDrawerTitle() {
            const name = _.get(this.media, 'video.source.preferred_name')
                || _.get(this.media, 'video.source.name')
                || _.get(this.media, 'video.basename');
            return name ? `${this.$t('AI labelling')} - ${name}` : this.$t('AI labelling');
        },
        // The shop-wide default, so "inherit" is not a blind choice.
        shopAiLabelDisplay() {
            return _.get(useSetupStore().activeCms, 'generativeMediaSettings.ai_label_display') || 'permanent';
        },
        // The active HLS file: the freshly-converted one (local) wins over whatever the
        // media prop arrived with. All HLS-dependent computeds read THIS, so they update
        // the moment localHls is set.
        activeHls() {
            return this.localHls || _.get(this.media, 'video.hls') || null;
        },
        // Re-keys HlsVideoPlayer so Vue fully recreates it when the source flips (e.g.
        // MP4 -> HLS once conversion finishes), giving a clean videojs init per source.
        playerSourceKey() {
            const hlsId = _.get(this.activeHls, 'id');
            if (hlsId) return `hls-${hlsId}`;
            return `src-${_.get(this.media, 'video.id') || _.get(this.media, 'file.id') || 'x'}`;
        },
        videoInProductListFeatureEnabled() {
            let features = _.get(this.activeCms, 'config.template.features') || [];
            return !!features.find(el => el === 'video-in-product-list');
        },
        type() {
            return this.media.type;
        },
        file() {
            switch (this.type) {
                case 'file':
                    return _.get(this.media, 'file');

                case 'image':
                    return _.get(this.media, 'image.source');

                case 'video':
                    if (this.activeHls) {
                        return this.activeHls;
                    } else {
                        return _.get(this.media, 'video.source');
                    }
            }
        },
        // The active layer (crop / AI-edit) of an image, if any.
        activeVariant() {
            if (this.type !== 'image') return null;
            return _.get(this.media, 'image.activeVariant') || null;
        },
        // An unbaked crop the editor handed over for an image that has no persisted Image
        // yet (fresh upload, library pick). It lives only in settings.processing until the
        // product is saved.
        hasDeferredCrop() {
            if (this.type !== 'image') return false;
            const processing = _.get(this.media, 'image.settings.processing');
            return !!processing && (!!processing.w || processing.x != null);
        },
        hasVariants() {
            const variants = _.get(this.media, 'image.variants') || [];
            return variants.length > 1;
        },
        // Tile shows the ACTIVE layer's pixels; falls back to the source thumbnail.
        displaySrc() {
            // A DEFERRED crop (settings.processing, not yet baked because the image is
            // not persisted) can only be expressed by the crop-capable app route, so it
            // must win over any raw CDN url below: a static file ignores ?fit=crop, which
            // is why cropping a freshly picked library version looked like it did nothing.
            if (this.hasDeferredCrop) return this.src;
            // processedFile is the authoritative active-file pointer (the baked crop /
            // AI variant). Use it directly — the `activeVariant.kind` metadata can be
            // stale (a real crop is sometimes reported as kind 'original'), so relying
            // on it left the tile showing the uncropped source.
            const processedUrl = _.get(this.media, 'image.processedFile.url');
            if (processedUrl) return processedUrl;
            const variant = this.activeVariant;
            if (variant && variant.kind && variant.kind !== 'original') {
                const url = _.get(variant, 'file.url');
                if (url) return url;
            }
            // A duplicated tile carries a fresh client image id (`new:…`) but no upload or
            // library source the resize route can serve, so `src` would build a broken
            // /images/new:… URL. Fall back to the source's absolute url so the copy still
            // previews before the product is saved. (Uploads resolve via `src` already;
            // library refs keep their own id, so neither matches here.)
            const imageId = _.get(this.media, 'image.id');
            const isFreshClone = typeof imageId === 'string'
                && imageId.indexOf('new:') === 0
                && imageId.indexOf('new:file-library:') !== 0
                && !_.get(this.media, 'image.source.upload');
            if (isFreshClone) {
                const sourceUrl = _.get(this.media, 'image.source.url');
                if (sourceUrl) return sourceUrl;
            }
            return this.src;
        },
        // The baked file the tile currently SHOWS for an image: the active AI-edit / crop
        // (processedFile, or a non-original active variant) wins over the source. Download
        // uses this so the user gets the layer they have selected, not the untouched
        // original. Returns null for an unedited image (→ download falls back to source).
        activeImageFile() {
            if (this.type !== 'image') return null;
            const processed = _.get(this.media, 'image.processedFile');
            if (processed && processed.id) return processed;
            const variant = this.activeVariant;
            if (variant && variant.kind && variant.kind !== 'original') {
                const file = _.get(variant, 'file');
                if (file && file.id) return file;
            }
            return null;
        },
        // Names the video in the dialog title, same source as the labelling drawer.
        videoDialogTitle() {
            const name = _.get(this.media, 'video.source.preferred_name')
                || _.get(this.media, 'video.source.name')
                || _.get(this.media, 'video.basename');
            return name || this.$t('Video');
        },
        // Keeps the dialog player at the video's own aspect ratio instead of the tile's
        // square-ish box; falls back to 16:9 when the dimensions are unknown.
        dialogPlayerHeight() {
            const videoWidth = _.get(this.media, 'video.width');
            const videoHeight = _.get(this.media, 'video.height');
            const ratio = (videoWidth && videoHeight) ? (videoWidth / videoHeight) : (16 / 9);
            return _.round(840 / (isNaN(ratio) || !ratio ? 16 / 9 : ratio));
        },
        // The file a download hands out. `file` follows the PLAYER, so for a converted
        // video it points at the HLS playlist - useless as a download. The uploaded
        // source is what the user expects to get back.
        downloadSource() {
            if (this.type === 'video') {
                return _.get(this.media, 'video.source') || this.file;
            }
            return this.file;
        },
        width() {
            let width = 150;
            if (['image', 'video'].includes(this.type) && this.media[this.type]) {
                let mediaWidth = this.media[this.type].width;
                let mediaHeight = this.media[this.type].height;
                const variant = this.activeVariant;
                const processing = _.get(this.media, 'image.settings.processing');
                const hasProcessedFile = !!_.get(this.media, 'image.processedFile.url');
                if (this.type === 'image' && processing && processing.w && processing.h) {
                    // A deferred/lazy crop defines the tile's aspect (output-absolute w/h),
                    // and it wins: it is applied ON TOP of the active layer, so the layer's
                    // own dimensions describe the pixels BEFORE the crop. Preferring them
                    // left a cropped library pick drawn at the uncropped version's ratio.
                    mediaWidth = processing.w;
                    mediaHeight = processing.h;
                } else if (hasProcessedFile && variant && variant.width && variant.height) {
                    // Active baked variant (crop / AI) — its dimensions drive the tile aspect.
                    mediaWidth = variant.width;
                    mediaHeight = variant.height;
                }
                const ratio = mediaWidth / mediaHeight;
                if (!isNaN(ratio)) width = this.height * ratio;
            }
            return _.round(width);
        },
        height() {
            return 150;
        },
        src() {
            const storageRoute = _.trim(
                `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`,
                '/'
            );

            let fit;
            let table = 'uploads';

            let id = this.file.id;
            // Set when a library pick has to be served through the files route (see below);
            // its own name/hash have to travel with it.
            let libraryFile = null;

            switch (this.type) {
                case 'file':
                    if (!this.file.upload) table = 'files';
                    break;

                case 'image': {
                    // A library-selected image isn't persisted yet — its id is a
                    // `new:file-library:` token, so an /images/{id} URL can't be built. The
                    // chosen File already exists and carries a usable absolute URL on
                    // `source.url`; serve the tile straight from it (fresh uploads are
                    // excluded — their source has an `upload` and resolve via the uploads
                    // table below).
                    const imageId = _.get(this.media, 'image.id');
                    if (!this.file.upload && typeof imageId === 'string' && imageId.indexOf('new:file-library:') === 0) {
                        // With a deferred crop we must NOT return the raw url: it is a static
                        // file and ignores ?fit=crop. Serve the File through the crop-capable
                        // files route instead, and serve the file whose pixels are actually
                        // active - processedFile is the picked VERSION, so cropping a picked
                        // AI edit crops that edit rather than the untouched source.
                        const activeFile = _.get(this.media, 'image.processedFile') || this.file;
                        if (this.hasDeferredCrop && _.get(activeFile, 'id') && _.get(activeFile, 'name')) {
                            table = 'files';
                            id = activeFile.id;
                            libraryFile = activeFile;
                        } else {
                            const libraryUrl = _.get(this.file, 'url') || _.get(this.media, 'image.url');
                            if (libraryUrl) return libraryUrl;
                        }
                    }
                    // Reflect a deferred/lazy crop (settings.processing) in the tile, same
                    // as the content editor's ImageField — so a fresh upload's crop/rotate
                    // shows before the product is saved (the uploads endpoint honours it).
                    const processing = _.get(this.media, 'image.settings.processing');
                    if (processing && (processing.w || processing.x != null)) {
                        fit = getFitStringFromProcessObject(processing);
                        if (processing.rot) fit += `&rot=${encodeURIComponent(processing.rot)}`;
                        if (processing.flip) fit += `&flip=${encodeURIComponent(processing.flip)}`;
                    } else {
                        fit = 'fit&h=150';
                    }

                    if (!this.file.upload && !libraryFile) {
                        table = 'images';
                        id = this.media.image.id;
                    }
                    break;
                }

                case 'video':
                    if (!this.file.upload) {
                        table = 'videos';
                        id = this.media.video.id;

                        if (this.activeHls) {
                            const cdnEndpoint = _.trim(
                                `${process.env.VUE_APP_CDN_ENDPOINT}${process.env.VUE_APP_CDN_ROOT || ''}`,
                                '/'
                            );

                            return `${cdnEndpoint}/${this.activeHls.path}/${this.activeHls.name}`;
                        }
                    }
                    break;
            }

            const name = libraryFile ? libraryFile.name : this.file.name;
            const hash = libraryFile ? libraryFile.hash : this.file.hash;
            const token = AuthApi.getToken();
            let src = `${storageRoute}/${table}/${id}/${name}?hash=${hash}&token=${token}`;
            if (this.type === 'image' && fit) src += `&${fit}`;

            return src;
        },
        setupStore() {
            return useSetupStore();
        },
        shopProductStore() {
            return useShopProductStore(pinia);
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
    },
    methods: {
        // The thumbnail is a preview: hand playback over to the dialog and stop the
        // little player, so the sound never comes from two places at once.
        openVideoDialog() {
            if (this.videoDialogVisible) return;

            this.videoDialogVisible = true;

            const tilePlayer = this.$refs.tilePlayer;
            if (tilePlayer) {
                tilePlayer.pause();
            }
        },
        // Fired once the dialog transition is done and the player is actually on screen.
        startDialogPlayback() {
            const dialogPlayer = this.$refs.dialogPlayer;
            if (dialogPlayer) {
                dialogPlayer.play();
            }
        },
        // Art. 50(4): write the editor's declaration onto the media video so the
        // product Save carries it, exactly like an image's disclosure. Turning it off
        // clears the kind with it, so a video is never left silently marked.
        // Open on a snapshot, never on the live object.
        openAiDrawer() {
            const video = this.media.video || {};
            this.aiDraft = {
                ai_disclosure: video.ai_disclosure === true,
                ai_source_type: video.ai_source_type || null,
                ai_label_display: video.ai_label_display || null,
                ai_assessment: video.ai_assessment || null,
            };
            this.aiDrawerVisible = true;
        },
        setAiDraft(key, value) {
            if (!this.aiDraft) return;
            this.$set(this.aiDraft, key, value === undefined ? null : value);
            // Turning the disclosure off clears the kind with it, so a video is never
            // left carrying a term it no longer claims.
            if (key === 'ai_disclosure' && value !== true) {
                this.$set(this.aiDraft, 'ai_source_type', null);
            }
        },
        // The commit point: only here does the draft reach the parent state.
        commitAiDraft() {
            if (this.aiDraft && this.media.video) {
                ['ai_disclosure', 'ai_source_type', 'ai_label_display'].forEach(key => {
                    this.updateMediaVideoAi({ media: this.media, key, value: this.aiDraft[key] });
                });
            }
            this.aiDrawerVisible = false;
        },
        // Overridden by ArticleMedium, exactly like updateMediaVisibility: the tile is
        // shared, the store it writes to is not.
        updateMediaVideoAi(payload) {
            this.shopProductStore.updateFormMediaVideoAi(payload);
        },
        changeVisibility(scope) {
            const updateMediaVisibility = (media, scope, value) => {
                if (this.scope === 'stepper') {
                    media.settings.show[scope] = value;
                } else {
                    this.updateMediaVisibility({ media, scope, value });
                }
            };
            switch (scope) {
                case 'highlight':
                case 'list':
                    // among all media objects only one is allowed
                    if (this.type === 'image' || (this.type === 'video' && this.videoInProductListFeatureEnabled)) {
                        this.form.media.forEach(media => updateMediaVisibility(media, scope, false));
                        updateMediaVisibility(this.media, scope, true);
                    } else {
                        updateMediaVisibility(this.media, scope, false);
                    }
                    break;

                case 'detail':
                    // among all media objects at least one is required
                    if (!this.media.settings.show[scope]) {
                        updateMediaVisibility(this.media, scope, true);
                    } else if (this.form.media.filter(media => media.settings.show[scope] === true).length > 1) {
                        updateMediaVisibility(this.media, scope, false);
                    }
                    break;

                case 'disabled':
                    // if list or highlight or the single detail
                    // then it is not allowed to disable
                    if (this.media.settings.show[scope]) {
                        updateMediaVisibility(this.media, scope, false);
                    } else if (
                        !(
                            this.media.settings.show.highlight ||
                            this.media.settings.show.list ||
                            (this.media.settings.show.detail &&
                                this.form.media.filter(media => media.settings.show[scope] === true).length <= 1)
                        )
                    ) {
                        updateMediaVisibility(this.media, scope, true);
                    }
                    break;
            }

            // if (scope === 'disabled') {
            //     if (! this.media.settings.show.list && ! this.media.settings.show.highlight) {
            //         this.media.settings.show[scope] = !this.media.settings.show[scope]
            //     }
            // } else {
            //     if (scope === 'detail') {
            //     } else {
            //         this.form.media.forEach(media => media.settings.show[scope] = false)
            //         this.media.settings.show[scope] = true
            //     }
            // }
        },
        isDisabled(scope) {
            // Media coming from a production dump may have a null `settings`,
            // so read every flag defensively rather than dereferencing directly.
            const show = key => _.get(this.media, 'settings.show.' + key) === true;
            const countWithScope = key => this.form.media.filter(media => _.get(media, 'settings.show.' + key) === true).length;

            let disabled = false;
            switch (scope) {
                case 'highlight':
                case 'list':
                    // highlight and list can not be toggled
                    // only a click on a different highlight/list icon
                    // will deactivate another
                    if (show(scope)) disabled = true;
                    break;

                case 'detail':
                    // among all media objects at least one is required
                    if (show(scope) && countWithScope(scope) <= 1) disabled = true;
                    break;

                case 'disabled':
                    // if list or highlight or the single detail
                    // then it is not allowed to disable
                    if (
                        !show(scope) &&
                        (show('highlight') ||
                            show('list') ||
                            (show('detail') && countWithScope(scope) <= 1))
                    ) {
                        disabled = true;
                    }
                    break;
            }

            return disabled;
        },
        deleteMedia() {
            const i = _.findIndex(this.form.media, el => el.id === this.media.id);
            if (i >= 0) {
                if (this.scope === 'stepper') {
                    this.form.media.splice(i, 1);
                    // TODO
                    // update settings flags if necessary
                } else if (this.scope === 'form') {
                    this.markMediaAsDeleted(i);
                }
            }
        },
        restoreMedia() {
            const i = _.findIndex(this.form.media, el => el.id === this.media.id);
            if (i >= 0 && this.scope === 'form') {
                this.unmarkMediaAsDeleted(i);
            }
        },
        // Insert a fresh copy of this tile right after it. The clone gets new client
        // `new:` ids (media + image) so the backend mints SEPARATE records — letting the
        // duplicate be cropped / AI-edited independently of the original. The image's
        // `source` is kept, so the new image is rebuilt from the same file on Save; a
        // library reference (`new:file-library:`) is left intact so the copy keeps
        // resolving its preview. The unique list/highlight flags are not inherited.
        duplicateMedia() {
            if (this.media.deleted) return;
            const i = _.findIndex(this.form.media, el => el.id === this.media.id);
            if (i < 0) return;

            const clone = _.cloneDeep(this.media);
            clone.id = uniqid('new:');
            delete clone.deleted;
            const imageId = _.get(clone, 'image.id');
            const isLibraryRef = typeof imageId === 'string' && imageId.indexOf('new:file-library:') === 0;
            if (clone.image && !isLibraryRef) {
                clone.image.id = uniqid('new:');
            }
            _.set(clone, 'settings.show.list', false);
            _.set(clone, 'settings.show.highlight', false);

            this.form.media.splice(i + 1, 0, clone);
            // Renumber positions so the new tile keeps its place after Save.
            this.form.media.forEach((m, idx) => { m.position = idx + 1; });
        },
        // Download the original file behind this tile. The private-storage route serves an
        // attachment when `download` is set; a library image isn't on that route, so it is
        // opened from its absolute url instead.
        async downloadMedia() {
            if (this.media.deleted) return;

            try {
                const storageRoute = _.trim(
                    `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`,
                    '/'
                );
                const token = await ensureFreshToken();

                // 1) Active baked layer (AI edit / crop). These are Files, served from the
                //    files table — so the SELECTED variant is what gets downloaded.
                const active = this.activeImageFile;
                if (active && active.id) {
                    const name = active.name || 'image';
                    let url = `${storageRoute}/files/${active.id}/${encodeURIComponent(name)}?download&token=${token}`;
                    if (active.hash) url += `&hash=${active.hash}`;
                    return this.triggerDownload(url, name);
                }

                // 2) Library image (not on the storage route) — open its absolute url.
                const imageId = _.get(this.media, 'image.id');
                if (this.type === 'image' && !this.file.upload && typeof imageId === 'string' && imageId.indexOf('new:file-library:') === 0) {
                    const libraryUrl = _.get(this.file, 'url') || _.get(this.media, 'image.url');
                    if (libraryUrl) {
                        window.open(libraryUrl, '_blank');
                        return;
                    }
                }

                // 3) Untouched original source.
                const source = this.downloadSource;
                if (!source || !source.name) return;

                const name = source.name;
                let table = 'uploads';
                let id = source.id;
                if (this.type === 'image' && !source.upload) {
                    table = 'images';
                    id = this.media.image.id;
                } else if (this.type === 'video' && !source.upload) {
                    // Deliberately NOT the videos route: once the HLS conversion is done it
                    // only knows how to serve the playlist and answers a download with 500.
                    // The source is a File and hands itself out on the files route.
                    table = 'files';
                } else if (this.type === 'file' && !source.upload) {
                    table = 'files';
                }

                let url = `${storageRoute}/${table}/${id}/${encodeURIComponent(name)}?download&token=${token}`;
                if (source.hash) url += `&hash=${source.hash}`;
                this.triggerDownload(url, source.preferred_name || name || 'download');
            } catch (e) {
                this.$notify.error({
                    title: this.$t('Download failed'),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000
                });
            }
        },
        triggerDownload(url, filename) {
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        editImage() {
            if (this.media.deleted) return;
            // Emit the raw id: a freshly-uploaded image has a client `new:…` id (its
            // pixels live in the uploads table). parseInt() would turn that into NaN
            // and the editor would open empty — so keep the id as-is.
            const imageId = _.get(this.media, 'image.id');
            if (imageId) {
                this.$emit('edit-image', imageId);
            }
        },
        updateMediaVisibility(payload) {
            return this.shopProductStore.updateFormMediaVisibility(payload);
        },
        markMediaAsDeleted(index) {
            return this.shopProductStore.markFormMediaAsDeleted(index);
        },
        unmarkMediaAsDeleted(index) {
            return this.shopProductStore.unmarkFormMediaAsDeleted(index);
        },
        // HLS conversion finished — attach the new HLS file to the in-memory video.
        // `src`/`file` recompute to the HLS playlist and `playerSourceKey` flips, which
        // re-keys HlsVideoPlayer so Vue destroys the MP4 player and mounts a fresh HLS one
        // (a clean videojs init — the only thing that reliably plays HLS; swapping a live
        // player MP4 -> MSE corrupts the pipeline).
        onConversionCompleted(payload) {
            const hls = payload && payload.hls;
            if (!hls || !_.get(this.media, 'video')) return;

            // Set the LOCAL reactive copy — this reliably invalidates activeHls (and thus
            // src / file / playerSourceKey), flipping playerSourceKey so HlsVideoPlayer is
            // re-keyed and re-created on the HLS source (a clean videojs init). Also mirror
            // it onto the media prop so a later re-render keeps HLS.
            this.localHls = hls;
            if (_.get(this.media, 'video')) {
                this.media.video.hls = hls;
            }
        }
    }
};
</script>

<style lang="sass" scoped>

$border-radius: 5px

li.media
    &.deleted
        cursor: default
        video, figure
            opacity: .6
            filter: grayscale(1) blur(1px)
        .icons
            opacity: .6
            pointer-events: none
    float: left
    margin-right: .5rem
    margin-bottom: .5rem
    position: relative
    // Tile badge vocabulary. Kept at TILE level, not under `figure`: the video
    // branch has no <figure>, and its badges would otherwise be unstyled - the
    // hover-revealed actions would never become visible at all.
    .variant-badge
        position: absolute
        bottom: 6px
        left: 6px
        width: 22px
        height: 22px
        border-radius: 11px
        background: #6E26FF
        color: #fff
        display: flex
        align-items: center
        justify-content: center
        font-size: 11px
        box-shadow: 0 1px 4px rgba(0, 0, 0, .35)
        z-index: 2
        // Same badge, two jobs: it SHOWS whether the asset is labelled - purple when it
        // is, muted when it is not - and opens the labelling drawer. A separate state
        // badge beside an identical action badge said the same thing twice.
        &.is-action
            cursor: pointer
            transition: background .15s ease
            &:hover
                background: #6E26FF
        &.muted
            background: rgba(0, 0, 0, .65)
        // An edited image can also be a labelled one - both badges sit bottom-left, so
        // the second one steps aside instead of hiding under the first.
        &.offset
            left: 34px
    // Quick-action badges (duplicate / download), bottom-right. Mirror the
    // variant-badge look but stay hidden until the tile is hovered, and sit above the
    // edit-popover overlay so they remain clickable.
    .media-actions
        position: absolute
        bottom: 6px
        right: 6px
        display: flex
        gap: 6px
        opacity: 0
        transition: opacity .15s ease
        z-index: 4
        .action-badge
            width: 22px
            height: 22px
            border-radius: 11px
            background: rgba(0, 0, 0, .65)
            color: #fff
            display: flex
            align-items: center
            justify-content: center
            font-size: 11px
            cursor: pointer
            box-shadow: 0 1px 4px rgba(0, 0, 0, .35)
            transition: background .15s ease
            &:hover
                background: #6E26FF
    &:hover .media-actions,
    .image figure:focus-within .media-actions
        opacity: 1
    figure, video, .icons
        transition: .15s filter ease, .15s opacity ease
    figure
        margin: 0
        position: relative
        background: lighten($color-body, 3%)
        border-radius: $border-radius
        img
            border-radius: $border-radius
            border-bottom-left-radius: 0
            border-bottom-right-radius: 0
        // Minimal AI-edit progress: a small brand-purple ring over an opaque scrim,
        // matching the VideoConversionBadge palette. Sits above the edit popover so
        // the tile reads "busy" while the edit runs.
        .generation-overlay
            position: absolute
            top: 0
            left: 0
            right: 0
            bottom: 0
            display: flex
            align-items: center
            justify-content: center
            background: rgba(20, 20, 28, .62)
            backdrop-filter: blur(3px)
            border-radius: $border-radius
            border-bottom-left-radius: 0
            border-bottom-right-radius: 0
            z-index: 3
            // Text colour comes from el-progress's text-color PROP (inline style —
            // CSS can't override it); only the size needs a bump for the 54px circle.
            ::v-deep .el-progress__text
                font-size: 12px !important
        // Full-image hover overlay (pencil) for editable image tiles — mirrors the
        // business-card image hover. Only images render this figure (not videos).
        .edit-popover
            position: absolute
            top: 0
            left: 0
            right: 0
            bottom: 0
            background: rgba(0, 0, 0, .5)
            display: flex
            align-items: center
            justify-content: center
            opacity: 0
            transition: opacity .15s ease
            border-radius: $border-radius
            border-bottom-left-radius: 0
            border-bottom-right-radius: 0
            i
                color: #fff
                font-size: 24px
        &.editable
            cursor: pointer
            &:hover .edit-popover,
            &:focus-visible .edit-popover
                opacity: 1
            &:focus-visible
                outline: 2px solid $--color-primary
                outline-offset: 2px
        .action-badge:focus-visible
            outline: 2px solid #fff
            outline-offset: 1px
    ::v-deep
        .video-js.vjs-city-offer
            border-radius: $border-radius
            border-bottom-left-radius: 0
            border-bottom-right-radius: 0
            &:not(.vjs-playing) .vjs-tech
                pointer-events: none
            .vjs-control-bar
                border-bottom-left-radius: 0
                border-bottom-right-radius: 0
            video,
            .vjs-menu-button-popup .vjs-menu .vjs-menu-content
                border-radius: $border-radius
                border-bottom-left-radius: 0
                border-bottom-right-radius: 0
            // Same rule as in the dialog: the frame may be off by a pixel or the video
            // may report different dimensions than the tile computed - fit it, never
            // stretch it.
            video
                object-fit: contain

    .el-button.delete-media,
    .el-button.restore-media
        position: absolute
        top: 5px
        right: 5px
        z-index: 2
        width: 30px
        height: 30px
        opacity: 0
        transition: opacity .15s ease
    &:hover
        .el-button.delete-media,
        .el-button.restore-media
            opacity: 1
    .icons
        // position: absolute
        background: rgba(0,0,0, .8)
        backdrop-filter: blur(10px)
        bottom: 0
        left: 0
        width: 100%
        height: 30px
        padding: 4px 6px
        display: flex
        justify-content: center
        align-items: center
        border-bottom-left-radius: $border-radius
        border-bottom-right-radius: $border-radius
        a
            color: $color-skeleton
            &.disabled
                // pointer-events: none
                cursor: not-allowed
            &:not(.disabled):hover
                color: white
                i
                    opacity: 1
                    &.active
                        color: white
            i
                opacity: .35
                min-width: 15px
                &.active
                    color: $ci-color-primary
                    opacity: 1
                    &.warning
                        color: $--color-warning
            & + a
                margin-left: 10px
</style>
